import { EWorldMapMarkerType, EWorldMapSetType } from '__generated__/globalTypes';
import { DetailQuery_moment_connectedMarkers } from 'graphql/query/__generated__/DetailQuery';
import { useGoToMarker } from 'hooks/map/useGoToMarker';
import { useMarkerVisibilityView } from 'hooks/map/useMarkerVisibilityView';
import { mapGraphQLMarker } from 'libs/utils/mapping/worldGrahQLMaps';
import { useContext } from 'react';
import { CtxView } from 'session/ctxView';

export const getZoomPriorityMarker = (markers: DetailQuery_moment_connectedMarkers[]) => {
    return (
        markers.find((e) => e.type === EWorldMapMarkerType.V2_ICON) ??
        markers.find((e) => e.type === EWorldMapMarkerType.V2_VARIANT_ICON) ??
        markers.find((e) => e.type === EWorldMapMarkerType.INDOOR_STAND) ??
        markers[0]
    );
};

export const useZoomToMarker = (params?: { speed?: number; offsetY?: number }) => {
    const ctxView = useContext(CtxView);
    const visibleMapSetIds = ctxView.visibleMapSetIds;
    const markerVisibility = useMarkerVisibilityView();

    const goToMarker = useGoToMarker({
        mapRef: ctxView.mapRef,
        defaultZoom: ctxView.map?.showOnMapZoom,
        padding: Math.max(ctxView.mapRef.current?.getContainer().clientWidth / 5, 50),
        offsetY: params?.offsetY,
        animate: true,
        bearing: ctxView.map?.initial?.rotation || 0,
        speed: params?.speed
    });

    const zoomToMarker = (markerId: string) => {
        const marker = ctxView.allMarkers?.find((e) => e.worldMapMarkerId === markerId);
        if (!marker) return;
        const isVisible = markerVisibility.isMarkerVisibleInViewMap(marker);
        if (!isVisible) {
            const notVisibleSetsIdsWithMarker = ctxView.map.worldMapSets
                .filter((e) => e.type === EWorldMapSetType.FLOOR)
                .filter((e) => !visibleMapSetIds.includes(e.worldMapSetId))
                .filter((e) => e.markers.some((marker) => marker.worldMapMarkerId === markerId))
                .map((e) => e.worldMapSetId);
            const floors = ctxView.map.worldMapSets.filter(
                (e) => e.type === EWorldMapSetType.FLOOR
            );
            if (floors.length) {
                const floorsToTurnOff = floors.map((e) => e.worldMapSetId);
                const floorsToTurnOn = floors
                    .filter((e) => notVisibleSetsIdsWithMarker.includes(e.worldMapSetId))
                    .map((e) => e.worldMapSetId);
                ctxView.setVisibleMapSetIds(
                    visibleMapSetIds
                        .filter((e) => !floorsToTurnOff.includes(e))
                        .concat(floorsToTurnOn)
                );
            }
        }
        goToMarker(mapGraphQLMarker(marker));
    };

    const zoomToMarkers = (markers: DetailQuery_moment_connectedMarkers[]) => {
        const topPriorityMarker = getZoomPriorityMarker(markers ?? []);
        if (topPriorityMarker?.worldMapMarkerId) {
            zoomToMarker(topPriorityMarker.worldMapMarkerId);
        }
    };

    return {
        zoomToMarker,
        zoomToMarkers
    };
};
